import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Carte shadcn/ui au système Voxler Router : fond `card`, filet `border`,
 * rayon lg (8px), ombre shadow-sm très discrète, padding 16px.
 * Composants de maquette : `card`, `card-head`, `card-title`, `card-desc`,
 * `card-body`, `card-bar` (barre d'outils de tableau), `pager` sans pagination
 * (`CardFoot`).
 */
const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'min-w-0 rounded-lg border border-border bg-card text-card-foreground shadow-sm',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

/** En-tête empilé (titre puis description). Pour titre + actions, préférer `CardHead`. */
const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-0.5 px-4 pt-4', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2 ref={ref} className={cn('m-0 text-heading', className)} {...props} />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('m-0 text-caption text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

/** Corps de carte (`card-body`) : padding 16px. */
const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('p-4', className)} {...props} />,
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-2 border-t border-border px-4 py-3', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

interface CardHeadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Actions, légende de graphique, bouton : alignés à droite du titre. */
  actions?: React.ReactNode;
  /** Niveau du titre : h2 par défaut, h3 dans une fiche latérale ou une carte imbriquée. */
  as?: 'h2' | 'h3';
}

/** `card-head` : titre + description à gauche, actions à droite. */
function CardHead({
  title,
  description,
  actions,
  as = 'h2',
  className,
  ...props
}: CardHeadProps): React.JSX.Element {
  const Heading = as;
  return (
    <div
      className={cn('flex flex-wrap items-start justify-between gap-3 px-4 pt-4', className)}
      {...props}
    >
      <div className="min-w-0">
        <Heading className="m-0 text-heading">{title}</Heading>
        {description !== undefined && description !== null ? (
          <p className="m-0 mt-0.5 text-caption text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions !== undefined && actions !== null ? (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

interface CardBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** À gauche : en général `<TableTabs>` (onglets avec compteur). */
  start?: React.ReactNode;
  /** À droite : filtres, recherche, boutons `size="sm"`. */
  end?: React.ReactNode;
}

/** `card-bar` : barre d'outils d'un tableau, filet en bas, padding 12px. */
function CardBar({ start, end, className, children, ...props }: CardBarProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-b border-border p-3',
        className,
      )}
      {...props}
    >
      <div className="flex flex-wrap items-center gap-2">{start}</div>
      {end !== undefined && end !== null ? (
        <div className="flex flex-wrap items-center gap-2">{end}</div>
      ) : null}
      {children}
    </div>
  );
}

interface CardFootProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Résumé à gauche (« 5 clients actifs ce mois »). */
  start?: React.ReactNode;
  /** Note à droite (« Montants HT »). */
  end?: React.ReactNode;
}

/** `pager` sans pagination : pied de tableau avec résumé et note, texte caption. */
function CardFoot({ start, end, className, children, ...props }: CardFootProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-caption text-muted-foreground',
        className,
      )}
      {...props}
    >
      {start !== undefined ? <span>{start}</span> : null}
      {end !== undefined ? <span>{end}</span> : null}
      {children}
    </div>
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardHead,
  CardBar,
  CardFoot,
};
